/* eslint-disable jsx-a11y/alt-text */
import { gql } from '@apollo/client';
import { Box, Container, Flex, Input } from '@energiebespaarders/symbols';
import { useIsMobile } from '@energiebespaarders/symbols/hooks';
import { Search } from '@energiebespaarders/symbols/icons/solid';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { Image, StructuredText } from 'react-datocms';
import styled from 'styled-components';
import { BlockRenderer } from '~/components/DatoCms/BlockRenderer';
import CategoryCard from '~/components/FAQ/CategoryCard';
import FAQSearchResults from '~/components/FAQ/FAQSearchResults';
import SubNavbar from '~/components/FAQ/SubNavbar';
import ImageCard from '~/components/ImageCard';
import { useDebouncedValue } from '~/hooks/useDebounce';
import { useMeOptional } from '~/hooks/useMe';
import theme from '~/styles/theme';
import type { getAllTopics, getAllTopics_allFaqTopics } from '~/types/generated/cms/getAllTopics';
import type {
  getCategories,
  getCategories_allFaqCategories,
} from '~/types/generated/cms/getCategories';
import { CategoryFragment, TopicFieldsFragment } from 'src/datoCMS/fragments/fragments';
import { getDatoClient } from 'src/datoCMS/datoCmsClient';
import type { MyBlock } from 'src/datoCMS/types';
import type { StructuredTextGraphQlResponse } from 'react-datocms';
import type { ChangeEvent } from 'react';

const Banner = styled(Box)`
  width: 100%;
  background-image: linear-gradient(
    to right top,
    ${theme.colors.greenDark} 5%,
    ${theme.colors.green} 75%,
    ${theme.colors.goldLighter}
  );
`;

// TODO: Nextjs is introducing on demand revalidation, could look into it:
// https://nextjs.org/docs/basic-features/data-fetching/incremental-static-regeneration#on-demand-revalidation
export const revalidateInterval = 60;

const FAQPage: React.FC<{
  categories: getCategories_allFaqCategories[];
  topics: getAllTopics_allFaqTopics[];
}> = ({ categories, topics }) => {
  const [searchString, setSearchString] = useState('');
  const { me } = useMeOptional();
  const userIsCustomer = me?.__typename === 'Customer';
  const isMobile = useIsMobile();
  const router = useRouter();

  function returnParentCategoryTopics(parentCategory: getCategories_allFaqCategories) {
    return topics
      .filter(topic => topic.category?.parentCategory?.id === parentCategory.id)
      .slice(0, 5);
  }
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const searchWords = e.target.value.replace(/[^\w\s]/g, '');
    setSearchString(searchWords);
  }, []);

  const debouncedSearchString = useDebouncedValue(searchString, 200);

  const clearSearch = useCallback(() => {
    setSearchString('');
  }, [setSearchString]);

  // check to always render something incase CH or Marketing forget to manually set popular articles
  const hasPopularArticles = !!topics.filter(t => t.isHighlighted).length;
  const renderedPopularArticles = hasPopularArticles
    ? topics.filter(topic => topic.isHighlighted)
    : topics;

  return (
    <>
      <Head>
        <link rel="canonical" href="https://energiebespaarders.nl/faq" key="canonical" />
      </Head>
      <Flex flexWrap="wrap">
        <SubNavbar categories={categories} />
        <Banner style={{ height: 300 }} width={1} py={[5, 10]} px={[2, 2, 2, 0]}>
          <Container size="lg" style={{ height: '100%' }}>
            <Flex height="100%" flexDirection="column " justifyContent="center">
              {userIsCustomer && (
                <Box
                  className="h2"
                  style={{
                    margin: 0,
                    fontWeight: 500,
                    color: 'white',
                    lineHeight: 1,
                  }}
                >
                  Hi {me.firstName},
                </Box>
              )}

              <Box
                className="h1"
                style={{
                  marginBottom: 6,
                  lineHeight: 1,
                  color: 'white',
                }}
              >
                Waar kunnen we je mee helpen?
              </Box>

              <Box>
                <Input
                  type="text"
                  value={searchString}
                  onChange={handleChange}
                  placeholder="Stel een vraag"
                  addonSide="start"
                  icon={Search}
                  mb={0}
                  inputMode="search"
                />
                <FAQSearchResults searchString={debouncedSearchString} clearSearch={clearSearch} />
              </Box>
            </Flex>
          </Container>
        </Banner>

        <Box width={1} py={10} px={[2, 2, 2, 0]} style={{ background: theme.colors.transparent }}>
          <Container size="lg">
            <Box display="flex" flexDirection="column">
              <h2 style={{ textAlign: 'center' }}>Populaire artikelen</h2>
              <Flex flexWrap="wrap" mx={-2} py={5}>
                {renderedPopularArticles.slice(0, isMobile ? 3 : 6).map((t, i) => {
                  return (
                    <Box
                      style={{ cursor: 'pointer' }}
                      key={i}
                      px={2}
                      py={2}
                      display="flex"
                      width={[1, 1, 1 / 3, 1 / 3]}
                      onClick={() => router.push(`/faq/${t.category?.slug}/${t.slug}`)}
                    >
                      <ImageCard
                        responsiveImageData={t.image!.responsiveImage!}
                        alt="De klant bekijkt haar geadviseerde offertes"
                        height="150px"
                        title={t.title!}
                        headingType="h3"
                        maxLinesOfContent={5}
                        content={
                          <StructuredText
                            data={t.description?.value as unknown as StructuredTextGraphQlResponse}
                            renderBlock={b => (
                              <BlockRenderer block={b.record as unknown as MyBlock} />
                            )}
                          />
                        }
                      />
                    </Box>
                  );
                })}
              </Flex>
            </Box>
          </Container>
        </Box>

        {/* CATEGORY BUBBLES */}
        <Box
          width={[1, 1, 1, 1]}
          py={10}
          px={[2, 2, 2, 0]}
          style={{ background: theme.colors.grayLight }}
        >
          <Container size="lg">
            <Box display="flex" flexWrap="wrap" alignItems="center" flexDirection="column">
              <h2 style={{ textAlign: 'center' }}>Kies een onderwerp</h2>
              <Flex mt={4} width={[1, 1, 1, 1]} mx={-3} flexWrap="wrap" flexDirection="row">
                {categories.map((c, i) => {
                  const columns =
                    categories.filter(cat => cat.isPrimary === true).length % 4 === 0
                      ? [1, 1 / 2, 1 / 4, 1 / 4]
                      : [1, 1 / 2, 1 / 3, 1 / 3];

                  return (
                    c.isPrimary && (
                      <Flex
                        flexDirection="column"
                        alignItems="center"
                        px={3}
                        py={2}
                        textAlign="center"
                        onClick={() => router.push(`/faq/${c.slug}`)}
                        style={{ cursor: 'pointer' }}
                        key={i}
                        width={columns}
                      >
                        <Image
                          data={c.image!.responsiveImage!}
                          style={{ borderRadius: '100%', objectFit: 'cover' }}
                        />
                        <Box
                          mt={2}
                          style={{ fontSize: theme.type.scale[4], wordBreak: 'keep-all' }}
                        >
                          {c.title}
                        </Box>
                      </Flex>
                    )
                  );
                })}
              </Flex>
            </Box>
          </Container>
        </Box>

        <Box width={1} py={10} px={[2, 2, 2, 0]} style={{ background: theme.colors.grayLight }}>
          <Container size="lg">
            <Flex flexWrap="wrap" mx={-2} my={-2}>
              {categories.map(c => {
                const categoryTopics = topics?.filter(t => t?.category?.id === c.id);
                const parentCategoryTopics: getAllTopics_allFaqTopics[] = c.isParent
                  ? returnParentCategoryTopics(c)
                  : [];

                return (
                  <Box key={c.id} style={{ display: 'flex' }} width={[1, 1, 1 / 2]} p={2}>
                    <CategoryCard
                      topics={parentCategoryTopics.length ? parentCategoryTopics : categoryTopics}
                      category={c}
                    />
                  </Box>
                );
              })}
            </Flex>
          </Container>
        </Box>
      </Flex>
    </>
  );
};

export const GET_PARENT_CATEGORIES = gql`
  ${CategoryFragment}
  query getCategories {
    allFaqCategories(filter: { parentCategory: { exists: "false" } }) {
      ...CategoryFragment
    }
  }
`;

export const GET_ALL_TOPICS = gql`
  ${TopicFieldsFragment}
  query getAllTopics {
    allFaqTopics(first: 100) {
      ...TopicFieldsFragment
    }
  }
`;

export const getStaticProps = async () => {
  const client = getDatoClient();

  const { data: categories } = await client.query<getCategories>({
    query: GET_PARENT_CATEGORIES,
    fetchPolicy: 'network-only',
  });

  const { data: topics } = await client.query<getAllTopics>({
    query: GET_ALL_TOPICS,
    fetchPolicy: 'network-only',
  });

  const data = {
    categories: categories.allFaqCategories,
    topics: topics.allFaqTopics,
  };

  return {
    props: data,
    revalidate: revalidateInterval,
  };
};

export default FAQPage;
